import notHandledHere from './notHandledHere';
import {database, http, network, ObjectId, metadata} from 'lib_ui-services';
import upsert from './doingUpsert_namespace_relation';
import analyzeHttpResult from '../../../../utilities/analyzeHttpResult';
import log from '@sstdev/lib_logging';

export default {
    verb: 'doingGet',
    excludedNamespaceRelations: notHandledHere,
    description: 'Obtain the requested data from the database',
    //this is the actual logic:
    logic
};

const _p = {
    getQueryUrl: http.getQueryUrl,
    get: http.get,
    upsert: upsert.logic,
    getNetworkStatus: network.getStatus,
    getRelationMetadata: metadata.getRelationMetadata
};
export const _private = _p;

/**
 * @typedef {import("rulesengine.io").LoggingProvider} LoggingProvider
 * @typedef {import("rulesengine.io").WorkflowStack} WorkflowStack
 * @typedef {import("rulesengine.io").Context} Context
 */

/**
 * @param {{
 *   data: T;
 *   prerequisiteResults: object[];
 *   context: Context;
 *   workflowStack: WorkflowStack[];
 *   dispatch: (data:object,context:Context,awaitResult?:boolean)=>Promise<void|any>
 *   log: LoggingProvider
 * }} parameters
 * @returns {T}
 */
async function logic({ context, data }) {
    let { type, namespace, relation } = context;
    const persistContext = { ...context, verb: 'persist', eventId: undefined };
    //if the type is:
    // 'find': payload should contain a raw mongodb syntax query in the root or
    // in the query field of the payload.
    // 'get': payload should contain { _id }
    const db = database.get();
    let result;
    switch (type) {
        case 'predefinedRecords':
            // Not handled here.
            return data;
        case 'find': {
            //`data.query` would be preferred, but don't be picky...
            result = await db.find(data.query?.criteria ?? data.criteria ?? data.query ?? data, persistContext);
            if (result && result.length) {
                result = foundLogic(result, data);
            } else {
                // record was not found locally
                // if limitSyncSize relation, try to fetch from server if online.
                // errorOnAbsence=false (+ ?? {}) so a find on a relation that isn't in the
                // use-case metadata returns [] here instead of throwing "Cannot find relation".
                const { limitSyncSize } = _p.getRelationMetadata(namespace, relation, false) ?? {};
                if (!limitSyncSize) {
                    // Only attempt server fetch for limitSyncSize relations, otherwise return an empty array
                    result = [];
                    break;
                }
                // first check if we're online
                const networkStatus = await _p.getNetworkStatus();
                if (networkStatus.isOnline) {
                    const criteria = {
                        searchCriteria: data.query?.criteria ?? data.criteria ?? data.query ?? data
                    };
                    const correlationId = new ObjectId();
                    const url = _p.getQueryUrl(namespace, relation, criteria, correlationId);
                    try {
                        const httpResult = await _p.get(url);
                        result = analyzeHttpResult(httpResult);
                    } catch (error) {
                        // A 403 means the user lacks GET access to this relation; a 404 means the
                        // server doesn't know it. Neither is an error here - the local view is the
                        // source of truth and we simply have no server data to fall back on, so we
                        // degrade to []. Logging these at `error` produces noisy false alarms in the
                        // console (e.g. CICO views referencing transaction:checkOut without GET access).
                        // Mirrors the 403/404 handling in synchronization/getFromServer.js.
                        if (error.status === 403 || error.status === 404) {
                            log.debug(`No server data for ${namespace}:${relation} (status ${error.status}); falling back to local results.`);
                        } else {
                            log.error(`Error fetching from server for ${namespace}:${relation} with criteria ${JSON.stringify(criteria)}. Error: ${error}`);
                        }
                        result = [];
                        break;
                    }
                    if (result && result.length) {
                        // found on server, upsert to local db and continue with found logic
                        await _p.upsert({ data: { items: result }, context: { ...context, verb: 'upsert' } });
                        result = foundLogic(result, data);
                    } else {
                        // not found on server
                        result = [];
                    }
                } else {
                    // offline, can't check server
                    result = [];
                }
            }
            break;
        }
        case 'get':
            result = await db.getById(data.query ?? data, persistContext);
            break;
        case 'fullTextSearch': {
            // Removed - in favor of doingGet_item_item.js
            // TODO: Remove this case when all calls are gone.
            throw new Error('Not supported - perform normal get.');
        }
        default: {
            // Fallback to get if _id is in the payload and no type is in the context
            if (data._id != null || data.query?._id != null) {
                result = await db.getById(data.query ?? data, persistContext);
                break;
            } else {
                throw new Error(`Unexpected payload and context values for 'get_${namespace}_${relation}_${type}'.`);
            }
        }
    }
    result = result.filter(x => !!x);
    return { ...data, result };
}

function foundLogic(originalResult, data) {
    let result = originalResult;
    const orderBy = data.query?.orderBy ?? data.orderBy;
    if (orderBy) {
        result.sort(orderBy);
    }
    const page = data.query?.page ?? data.page;
    if (page) {
        result = result.slice(page.offset || 0, page.limit);
    }
    return result;
}
